import _ from 'underscore';
import queryString from 'query-string';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { ScopeV2 } from '@bingads-webui-campaign-react/with-site-map';

const pathNamePrefix = '/campaign/vnext/';

export const getPathName = pageItem => `${pathNamePrefix}${pageItem.route}`;

export function mapToPageItems(items, i18n, defaultLandingPage, isFallbackSelected) {
  return _.map(items, item => (item.type === 'divider' ? item : {
    id: item.id,
    content: item.content || i18n.getString(item.name),
    isSelected: _.isUndefined(item.isSelected) ? item.selected : item.isSelected,
    isPageEnabled: _.isUndefined(item.isPageEnabled) ? item.available : item.isPageEnabled,
    route: item.route,
    isDefaultLandingPage: item.id === defaultLandingPage,
    isFallbackSelected: isFallbackSelected(item),
    defaultLandingPageTooltipText: item.defaultLandingPageTooltipText ||
      curlyTemplate(i18n.getString(_TL_('Set {{pageName}} as your default landing page')))({ pageName: i18n.getString(item.name) }),
    showTag: item.showTag,
    tagClass: item.tagClass,
    i18n,
    children: item.children,
    scopeV2: item.scopeV2,
  }));
}

export function getPageItems(pageGroups, siteMap, i18n, defaultLandingPage, isFallbackSelected) {
  return _.reduce(pageGroups, (result, group, index) => {
    const newItems = mapToPageItems(_.filter(siteMap, item => group.indexOf(item.id) >= 0), i18n, defaultLandingPage, isFallbackSelected);
    const divider = (index === 0 || _.isEmpty(newItems)) ? [] :
      [{ id: `divider_${index}`, type: 'divider' }];

    return _.union(result, divider, newItems);
  }, []);
}

export const getTrimmedParams = (item, scope) => {
  const searchObj = queryString.parse(scope.history.location.search);
  const trimmedParams = ScopeV2.trimToScope(item.scopeV2, searchObj);
  return trimmedParams;
};

export const getTrimmedQuery = (item, scope) => {
  const trimmedParams = getTrimmedParams(item, scope);
  return queryString.stringify(trimmedParams, { sort: false });
};

export const getPageItemUrl = (pageItem, scope) => {
  if (!pageItem.isPageEnabled) {
    return '';
  }

  const path = getPathName(pageItem);
  const query = getTrimmedQuery(pageItem, scope);

  return `${path}?${query}`;
};

// only flatten {limitLevel} depth
export const getFlattenedItems = (items, noExpandGroups, limitLevel = 2) =>
  items.reduce((flattenedItems, item) => {
    if (!item.children || item.children.length === 0 || limitLevel <= 1 || _.contains(noExpandGroups, item.id)) {
      return [...flattenedItems, item];
    }

    return [
      ...flattenedItems,
      ...getFlattenedItems(item.children, noExpandGroups, limitLevel - 1),
    ];
  }, []);

const fillGroupCount = groups => _.map(groups, (group) => {
  if (group.count) {
    return group;
  }

  const newgroup = _.extend(group, {
    children: fillGroupCount(group.children),
  });
  return _.extend(newgroup, {
    count: _.reduce(newgroup.children, (sum, child) => sum + child.count, 0),
  });
});

const fillGroupStartIndex = (groups, startIndex = 0) => _.map(groups, (group, index) => {
  const si = index === 0 ? startIndex : groups[index - 1].startIndex + groups[index - 1].count;
  _.extend(group, {
    children: fillGroupStartIndex(group.children, si),
  });
  return _.extend(group, {
    startIndex: si,
  });
});

const fillSelectedAndCollapse = (groups, falttenedItems, selectedIndex = -1) => {
  const si = selectedIndex < 0 ? _.findIndex(falttenedItems, { isSelected: true }) : selectedIndex;
  return _.map(groups, (group) => {
    if (si >= group.startIndex && si < group.startIndex + group.count) {
      return _.extend({}, group, { isSelected: true });
    }

    if (group.count === 1) {
      return group;
    }

    const newChildren = fillSelectedAndCollapse(group.children, falttenedItems, si);
    return _.extend({}, group, { isCollapsed: true, children: newChildren });
  });
};

export const getGroupsForDetailList = (items, falttenedItems, noExpandGroups, level = 0, limitLevel = 1) => {
  const groupsResult = _.reduce(items, (groups, item) => {
    let group;
    const name = item.content || (_.find(falttenedItems, { id: item.id })).content;
    // the last condition is for special mistake configs, we should not have this kind of configs. just in case
    if (_.contains(noExpandGroups, item.id) || item.type === 'divider' || (_.isEmpty(item.children) && level === 0)) {
      group = [{
        key: `group-${item.id}`,
        keyId: item.id,
        count: 1,
        children: [],
        level,
        name,
      }];
    } else if (_.isEmpty(item.children) || level + 1 > limitLevel) {
      group = [];
    } else if (item.children.length > 0 && item.children[0].children && item.children[0].children.length > 0 && level + 1 < limitLevel) {
      const children = getGroupsForDetailList(item.children, falttenedItems, noExpandGroups, level + 1, limitLevel);
      group = [{
        key: `group-${item.id}`,
        keyId: item.id,
        level,
        children,
        name,
      }];
    } else {
      group = [{
        key: `group-${item.id}`,
        keyId: item.id,
        count: item.children.length,
        children: [],
        level,
        name,
      }];
    }

    return [
      ...groups,
      ...group,
    ];
  }, []);
  return level === 0 ? fillSelectedAndCollapse(fillGroupStartIndex(fillGroupCount(groupsResult)), falttenedItems) :
    groupsResult;
};

export const getSingleItems = (groups, items) => _.reduce(groups, (singleItems, group) => {
  if (group.count === 1) {
    return [...singleItems, _.extend({}, items[group.startIndex], { parentId: group.keyId })];
  }
  return singleItems;
}, []);
